import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import {
  TypographyLarge,
  TypographyNormal,
  TypographySmall,
} from "../../customComponent";
import { inputOnlyNumbers } from "../../../helpers/inputProcessing";
import {
  bplrtOptions,
  getPublicHtmlAsset,
} from "../../../externalFeatures/components/bplrt";
import { logger } from "../../../helpers/logger";
import { v4 as uuidv4 } from "uuid";
import { setBPLRTComponents } from "../../../store/actions/cmsAction";

const StyledBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  ":last-child": {
    marginBottom: 0,
  },
}));

const BPLRTComponentSetting = ({
  open,
  handleClose,
  addContent,
  container,
  editContent,
  saveEditChanges,
  assetCount,
}) => {
  const dispatch = useDispatch();
  const { layerMedias, bplrtComponents } = useSelector((state) => state.cms);

  const { t } = useTranslation("cms");
  const { t: tCommon } = useTranslation();

  const [componentIndex, setComponentIndex] = useState(null);
  const [duration, setDuration] = useState(10);

  const [optionType, setOptionType] = useState("BPLRT_EQUIPMENT");
  const [scheduleStation, setScheduleStation] = useState("BP1");

  useEffect(() => {
    if (open) {
      setComponentIndex(null);
      setDuration(10);

      if (editContent) {
        const index = layerMedias[container.id].findIndex(
          (media) => media.id === editContent.id
        );

        setComponentIndex(index);
        setDuration(editContent.interval);
        setOptionType(editContent.type);
      }
    }

    return () => {
      setOptionType("BPLRT_EQUIPMENT");
      setScheduleStation("BP1");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCloseDialog = async () => {
    handleClose();
  };

  const handleOptionType = (e) => {
    const value = e.target.value;
    setOptionType(value);
  };

  const handleSubmitAdContent = (componentType) => {
    const addedContent = {
      id: uuidv4(),
      assetID: componentType.assetID,
      interval: parseInt(duration),
      appType: "bplrt",
      type: componentType.value,
      name: t(`bplrt.component${componentType.label}`),
    };

    logger.log(`handleSubmitAdContent:`, addedContent);
    const updatedMedias = {};
    const updatedOrder = [];
    updatedMedias[addedContent.id] = addedContent;
    updatedOrder.push(addedContent.id);
    addContent(updatedMedias, updatedOrder);
  };

  const handleSaveEdit = (componentType) => {
    const edited = {
      id: editContent.id,
      assetID: componentType.assetID,
      interval: parseInt(duration),
      appType: "bplrt",
      type: componentType.value,
      name: t(`bplrt.component${componentType.label}`),
    };

    const editedComponents = { ...bplrtComponents };
    editedComponents[editContent.id]["appType"] = componentType.value;
    dispatch(setBPLRTComponents(editedComponents));

    logger.log(`handleSaveEdit:`, edited);
    saveEditChanges(componentIndex, edited);
    handleClose();
  };

  const submitChanges = () => {
    const componentType = bplrtOptions[optionType];
    if (!editContent) {
      handleSubmitAdContent(componentType);
    } else {
      handleSaveEdit(componentType);
    }
  };

  const parseDialogTitle = useMemo(() => {
    if (!editContent) {
      return t("bplrt.addSettingTitle");
    } else {
      return t("bplrt.editSettingTitle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContent]);

  const parseContentSubmitText = useMemo(() => {
    if (!editContent) {
      return tCommon("submitBtn");
    } else {
      return tCommon("saveBtn");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContent]);

  const previewElement = useMemo(() => {
    const componentHTMLSrc = getPublicHtmlAsset(
      bplrtOptions[optionType].htmlSrc
    );
    switch (optionType) {
      case "BPLRT_EQUIPMENT":
        return (
          <iframe
            draggable={false}
            title={`EQUIPMENT`}
            src={componentHTMLSrc}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
              zoom: 0.33,
            }}
          />
        );

      case "BPLRT_EQUIPMENT_TABLE":
        return (
          <iframe
            draggable={false}
            title={`EQUIPMENT_TABLE`}
            src={componentHTMLSrc}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
              zoom: 0.33,
            }}
          />
        );

      case "BPLRT_SCHEDULE":
        return (
          <iframe
            ref={(el) => {
              if (el) {
                setTimeout(() => {
                  el.contentWindow.postMessage({
                    type: "LOADED",
                    payload: scheduleStation,
                  });
                }, 777);
              }
            }}
            draggable={false}
            title={`SCHEDULE`}
            src={componentHTMLSrc}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
              zoom: 0.33,
            }}
          />
        );

      case "BPLRT_SERVICE_INFO":
        return (
          <iframe
            draggable={false}
            title={`SERVICE_INFO`}
            src={componentHTMLSrc}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
              zoom: 0.33,
            }}
          />
        );

      case "BPLRT_SMP":
        return (
          <iframe
            draggable={false}
            title={`SMP`}
            src={componentHTMLSrc}
            width={"100%"}
            height={"100%"}
            scrolling={"no"}
            style={{
              border: "none",
              overflow: "hidden",
              pointerEvents: "none",
              zoom: 0.33,
            }}
          />
        );

      default:
        return (
          <Box
            width={"100%"}
            sx={{
              height: "250px",
              backgroundColor: "black",
            }}
          ></Box>
        );
    }
  }, [optionType, scheduleStation]);

  const enableDuration = useMemo(() => {
    if (assetCount > 1) {
      return true;
    } else if (assetCount === 1) {
      return !editContent ? true : false;
    } else {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetCount, editContent]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={true}
      onClose={() => handleCloseDialog()}
    >
      <DialogTitle
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        {parseDialogTitle}
        <IconButton onClick={() => handleCloseDialog()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ minHeight: "33vh" }}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          sx={{ mt: 3, px: 3 }}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            sx={{ p: 4, width: "100%" }}
          >
            <Box
              display={"flex"}
              justifyContent={"left"}
              alignItems={"center"}
              sx={{
                py: 2,
                px: 4,
                width: "100%",
                border: "1px solid black",
                borderRadius: "22px",
              }}
            >
              <Box
                width={"50%"}
                display={"flex"}
                flexDirection={"column"}
                gap={1}
              >
                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("bplrt.componentLabel")}
                    </TypographyLarge>
                  </Box>

                  <Box width={"70%"} display={"flex"} alignItems={"center"}>
                    <TextField
                      margin="dense"
                      fullWidth
                      variant="outlined"
                      size="small"
                      select
                      SelectProps={{
                        native: true,
                      }}
                      value={optionType}
                      onChange={(e) => {
                        handleOptionType(e);
                      }}
                      sx={{ pl: 2 }}
                    >
                      {Object.keys(bplrtOptions).map((opt) => {
                        const option = bplrtOptions[opt];
                        return (
                          <option value={opt} key={opt}>
                            {t(`bplrt.component${option.label}`)}
                          </option>
                        );
                      })}
                    </TextField>
                  </Box>
                </StyledBox>

                <StyledBox>
                  <Box display={"flex"} gap={"3px"} alignItems={"center"}>
                    <TypographyLarge sx={{ ml: 1 }}>
                      {t("bplrt.durationLabel")}
                    </TypographyLarge>
                    <Tooltip
                      arrow
                      disableInteractive
                      placement="top"
                      title={
                        <TypographySmall
                          sx={{ color: "white", textAlign: "center" }}
                        >
                          {t("bplrt.durationTip")}
                        </TypographySmall>
                      }
                    >
                      <InfoOutlinedIcon fontSize="10px" />
                    </Tooltip>
                  </Box>

                  <Box
                    width={"50%"}
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                  >
                    <Tooltip
                      arrow
                      disableInteractive
                      title={
                        enableDuration ? (
                          ""
                        ) : (
                          <TypographySmall
                            sx={{ color: "white", textAlign: "center" }}
                          >
                            {t("setting.durationDisabledTip")}
                          </TypographySmall>
                        )
                      }
                    >
                      <TextField
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        size="small"
                        value={duration}
                        disabled={!enableDuration}
                        onChange={(e) => setDuration(e.target.value)}
                        onKeyDown={(e) => inputOnlyNumbers(e)}
                        sx={{ pl: 2 }}
                      />
                    </Tooltip>
                    <TypographyNormal flex={"none"}>
                      {t("setting.durationHint")}
                    </TypographyNormal>
                  </Box>
                </StyledBox>
              </Box>

              <Box width={"50%"} display={"flex"} flexDirection={"column"}>
                <Box width={"100%"} sx={{ py: 1, px: 2 }}>
                  <TypographyLarge sx={{ mb: 1, ml: 1 }}>
                    {t("bplrt.previewLabel")}
                  </TypographyLarge>
                  <Box
                    width={"100%"}
                    display={"flex"}
                    alignContent={"center"}
                    sx={{ height: "240px" }}
                  >
                    {previewElement}
                  </Box>

                  {/* TODO: if need preview station schedule */}
                  {/* {optionType === "BPLRT_SCHEDULE" && (
                    <Box
                      width={"100%"}
                      display={"flex"}
                      alignContent={"center"}
                    >
                      {selectScheduleElement}
                    </Box>
                  )} */}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ mx: 2, mb: 1 }}>
        <>
          <Button variant="outlined" onClick={() => handleCloseDialog()}>
            {tCommon("cancelBtn")}
          </Button>
          <Button variant="contained" onClick={() => submitChanges()}>
            {parseContentSubmitText}
          </Button>
        </>
      </DialogActions>
    </Dialog>
  );
};

export default BPLRTComponentSetting;
